import { logger } from '@/lib/logger';
import { supabase, SUPABASE_ANON_KEY, SUPABASE_URL } from '@/lib/supabase';

/**
 * Client for the `wp-autologin` Edge Function.
 *
 * Returns a one-time WordPress login URL that the community WebView loads as
 * its initial request: WP sets its session cookies and redirects into
 * /community/<path>, where the Circle iframe auto-authenticates against the
 * WP session.
 *
 * Raw fetch (not supabase.functions.invoke) so the HTTP status survives:
 * 403 not_member drives the native paywall instead of a generic error.
 */

export type CommunityAutologinResult =
  | { status: 'ok'; loginUrl: string }
  | { status: 'not_member' }
  | { status: 'error'; message: string };

export async function fetchCommunityAutologinUrl(next: string): Promise<CommunityAutologinResult> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    logger.error('[communityAutologin] Supabase not configured');
    return { status: 'error', message: 'Community is unavailable right now.' };
  }

  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      return { status: 'error', message: 'You need to be signed in to open The Hang.' };
    }

    const resp = await fetch(`${SUPABASE_URL}/functions/v1/wp-autologin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ next }),
    });

    let data: any = null;
    try {
      data = await resp.json();
    } catch {
      // Non-JSON body (e.g. gateway error).
    }

    if (resp.ok && data?.ok && typeof data.login_url === 'string') {
      return { status: 'ok', loginUrl: data.login_url };
    }
    if (resp.status === 403 && data?.error === 'not_member') {
      return { status: 'not_member' };
    }

    logger.error('[communityAutologin] Failed:', resp.status, data?.error);
    return { status: 'error', message: 'Could not open The Hang. Please try again.' };
  } catch (error) {
    logger.error('[communityAutologin] Network error:', error);
    return { status: 'error', message: 'Network error. Please check your connection and try again.' };
  }
}
